import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { isSandboxAutoSignIn } from '../../api/General/SandboxDemo';

/**
 * Sandbox demo band (sandbox.mowazaf.com only).
 *
 * Every visitor on the public sandbox is signed into ONE shared demo HR account, and the whole company is wiped and
 * re-seeded every night. A prospect has to be told both facts before they type anything, and needs a way back to
 * the real thing: create an account on the live app, or talk to sales.
 *
 * Renders null everywhere else: it asks the same anonymous `SandboxDemo/status` endpoint the landing page uses,
 * which answers true only when the server's SandboxDemo gate is open (App Service flag AND the mowazafsandbox
 * database). One request per page load, cached for the session of the module.
 *
 * Copy rule (owner 2026-09-23): the entry tier is "Mowazaf Connect" — the word "Free" must not appear here.
 */

const SIGNUP_URL = 'https://app.mowazaf.com/signup?ref=mowazaf.com';
const CONTACT_URL_EN = 'https://mowazaf.com/contact/';
const CONTACT_URL_AR = 'https://mowazaf.com/ar/contact/';
const DISMISS_KEY = 'mowazaf.sandboxBanner.dismissed';

let cachedStatus: Promise<boolean> | null = null;

const SandboxDemoBanner: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isSandbox, setIsSandbox] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let alive = true;
    if (!cachedStatus) cachedStatus = isSandboxAutoSignIn();
    cachedStatus.then((v) => {
      if (alive) setIsSandbox(v);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!isSandbox || dismissed) return null;

  const contactUrl = (i18n.language || 'en').startsWith('ar') ? CONTACT_URL_AR : CONTACT_URL_EN;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* storage blocked: dismissal lasts for this render tree only */
    }
  };

  return (
    <div
      role="status"
      data-testid="sandbox-demo-banner"
      className="border-b border-primary/20 bg-primary/5 px-4 py-2.5 md:px-6"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-2.5">
          <Icon name="Info" size={16} className="mt-0.5 shrink-0 text-primary" />
          <p className="text-sm text-foreground">
            <span className="font-semibold">{t('sandboxDemo.title')}</span>{' '}
            <span className="text-muted-foreground">{t('sandboxDemo.body')}</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <a
            href={SIGNUP_URL}
            target="_blank"
            rel="noopener"
            data-testid="sandbox-demo-banner-signup"
            className="inline-flex min-h-[36px] items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t('sandboxDemo.createAccount')}
          </a>
          <a
            href={contactUrl}
            target="_blank"
            rel="noopener"
            data-testid="sandbox-demo-banner-sales"
            className="inline-flex min-h-[36px] items-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t('sandboxDemo.talkToSales')}
          </a>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('sandboxDemo.dismiss') as string}
            title={t('sandboxDemo.dismiss') as string}
            className="inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SandboxDemoBanner;
